import {Cursor} from "./cursor";

abstract class Expression {
    abstract resolve(cursor: Cursor): any;
    static parse(tokens: IToken[]): Literal | undefined {
        return Operator.parse(tokens) || Literal.parse(tokens);
    }
}
abstract class Literal extends Expression {
    static parse(tokens: IToken[]): Literal | undefined {
        return StringLiteral.parse(tokens) || BooleanLiteral.parse(tokens) || NumberLiteral.parse(tokens) ||
            VariableLiteral.parse(tokens) || CompoundLiteral.parse(tokens);
    }
}
abstract class Operator extends Expression {
    static parse(tokens: IToken[]): Literal | undefined {
        return Binary.parse(tokens) || Unary.parse(tokens)
    }
}
abstract class Binary extends Literal {
    protected constructor(protected left: Literal, protected right: Literal) {
        super();
    }

    abstract resolve(cursor: Cursor): any;
    static parse(tokens: IToken[]): Literal | undefined {
        if (tokens.length < 3) return;
        const working = [...tokens];
        const left = Literal.parse(working);
        const operator = working.shift();
        const right = Literal.parse(working);

        if (left && right && operator) {
            while (tokens.length > working.length)
                tokens.shift();
            if (operator.token === "add") {
                return new Add(left, right);
            } else if (operator.token === "subtract") {
                return new Subtract(left, right);
            } else if (operator.token === "multiply") {
                return new Multiply(left, right);
            } else if (operator.token === "divide") {
                return new Divide(left, right);
            } else if (operator.token === "and") {
                return new And(left, right);
            } else if (operator.token === "or") {
                return new Or(left, right);
            } else if (operator.token === "equals") {
                return new Equals(left, right);
            } else if (operator.token === "notEquals") {
                return new NotEquals(left, right);
            } else {
                throw new Error(`Unknown binary operator ${operator.token}`)
            }
        }
        return;
    }
}
class Equals extends Binary {
    resolve(cursor: Cursor): any {
        return this.left.resolve(cursor) === this.right.resolve(cursor);
    }
}
class NotEquals extends Binary {
    resolve(cursor: Cursor): any {
        return this.left.resolve(cursor) !== this.right.resolve(cursor);
    }
}
class Add extends Binary {
    resolve(cursor: Cursor): any {
        return Number(this.left.resolve(cursor)) + Number(this.right.resolve(cursor));
    }
}
class Subtract extends Binary {
    resolve(cursor: Cursor): any {
        return Number(this.left.resolve(cursor)) - Number(this.right.resolve(cursor));
    }
}
class Multiply extends Binary {
    resolve(cursor: Cursor): any {
        return Number(this.left.resolve(cursor)) * Number(this.right.resolve(cursor));
    }
}
class Divide extends Binary {
    resolve(cursor: Cursor): any {
        return Number(this.left.resolve(cursor)) / Number(this.right.resolve(cursor));
    }
}
class And extends Binary {
    resolve(cursor: Cursor): any {
        return Boolean(this.left.resolve(cursor)) && Boolean(this.right.resolve(cursor));
    }
}
class Or extends Binary {
    resolve(cursor: Cursor): any {
        return Boolean(this.left.resolve(cursor)) || Boolean(this.right.resolve(cursor));
    }
}
abstract class Unary extends Literal {
    protected constructor(protected value: Literal) {
        super();
    }

    abstract resolve(cursor: Cursor): boolean;
    static parse(tokens: IToken[]): Literal | undefined {
        if (tokens.length < 2) return;
        if (tokens[0].token === "not") {
            tokens.shift();
            const value = Literal.parse(tokens);
            
            if (value !== undefined) {
                return new Not(value);
            }
            throw new Error(`Badly formed unary grammar`);
        }
        return;
    }
}
class Not extends Unary {
    resolve(cursor: Cursor) {
        return ! Boolean(this.value.resolve(cursor));
    }
}
class StringLiteral extends Literal {
    constructor(private readonly value: string) {
        super();
    }
    resolve(cursor: Cursor) {
        return this.value;
    }
    static parse(tokens: IToken[]): Literal | undefined {
        if (tokens.length === 0) return;
        if (tokens[0].token === "string") {
            // @ts-ignore replaceAll compile errors on skybox build
            return new StringLiteral(tokens.shift()!.value.replaceAll("\"", ""));
        }
        return;
    }
}
class BooleanLiteral extends Literal {
    constructor(private readonly value: boolean) {
        super();
    }
    resolve(cursor: Cursor) {
        return this.value;
    }
    static parse(tokens: IToken[]): Literal | undefined {
        if (tokens.length === 0) return;
        if (tokens[0].token === "boolean") {
            return new BooleanLiteral(tokens.shift()!.value.toLowerCase() === "true");
        }
        return;
    }
}
class NumberLiteral extends Literal {
    constructor(private readonly value: number) {
        super();
    }
    resolve(cursor: Cursor) {
        return this.value;
    }
    static parse(tokens: IToken[]): Literal | undefined {
        if (tokens.length === 0) return;
        if (tokens[0].token === "number") {
            return new NumberLiteral(Number(tokens.shift()!.value))
        }
        return;
    }
}
class VariableLiteral extends Literal {
    constructor(private readonly value: string) {
        super();
    }
    resolve(cursor: Cursor) {
        const register = cursor.registers.get(this.value);

        if (register) {
            return register.get();
        } else {
            throw new Error(`Failed to get a value for register named ${this.value}`)
        }
    }
    static parse(tokens: IToken[]): Literal | undefined {
        if (tokens.length === 0) return;
        if (tokens[0].token === "variable") {
            return new VariableLiteral(tokens.shift()!.value)
        }
        return;
    }
}
class CompoundLiteral extends Literal {
    protected constructor(protected compound: Operator) {
        super();
    }
    resolve(cursor: Cursor): any {
        return this.compound.resolve(cursor);
    }

    static parse(tokens: IToken[]): Literal | undefined {
        if (tokens.length < 3) return;
        if (tokens[0].token === "left") {
            tokens.shift();
            const compound = Operator.parse(tokens);

            if (compound && tokens.length > 0 && tokens.shift()!.token === "right") {
                return new CompoundLiteral(compound);
            } else {
                throw new Error(`Failed to parse a compound token, maybe an unclosed bracket?`);
            }
        }
        return
    }
}
interface IToken {
    token: string,
    value: string
}
const tokens: {[key: string]: RegExp} = {
    multiply: /^(\*)/,
    divide: /^(\/)/,
    add: /^(\+)/,
    subtract: /^(-)/,
    left: /^(\()/,
    right: /^(\))/,
    equals: /^(==?)/,
    notEquals: /^([!^]=)/,
    and: /^(and|AND|&&)\W/,
    or: /^(or|OR|\|\|)\W/,
    not: /^(not|NOT|!)/,
    string: /^("[a-zA-Z]\w+")/,
    boolean: /^(true|TRUE|false|FALSE)/,
    number: /^(\d+(?:\.\d+)?)/,
    variable: /^([a-zA-Z]\w+)/,
}
const tokenise = (expression: string): IToken[] => {
    const testTokens = (token: string) => tryMatch(tokens[token], working, (match) => {
        const value = match[1];

        results.push({token, value})
        working = working.substr(value.length).trim();
    });
    const results: IToken[] = [];
    let working = expression.trim();

    while(working.length) {
        if (! Object.keys(tokens).some(testTokens)) {
            throw new Error(`Failed to parse ${expression} stopped at ${working}`);
        }
    }
    return results;
}
const parse = (expression: string): Literal => {
    const literal = Expression.parse(tokenise(expression));

    if (literal) {
        return literal;
    }
    throw new Error(`Failed to parse grammar of ${expression}`);
}
const tryMatch = (pattern: RegExp, test: string, callback: (result: RegExpMatchArray) => void): boolean => {
    const match = test.match(pattern);

    if (match) {
        callback(match);
        return true;
    }
    return false;
};
export function testExpression(expression: string, cursor: Cursor): boolean {
    return Boolean(parse(expression).resolve(cursor));
}